import { isNil, sum } from 'lodash';
import { Matrix3, Vector3 } from 'three';
import { Nullable } from '../interfaces/common/i-generics';
import { IImperialCoordinate } from '../interfaces/i-imperial';

/** in feet per second */
export const GRAVITY_FT_PER_SEC = 32.174;

/** 1 miles per hour => 1.46667 feet per second */
export const MPH_TO_FTPS = 1.46667;

export const FTPS_TO_MPH = 1 / MPH_TO_FTPS;

/** 1 foot  => 12 inches */
export const FT_TO_INCHES = 12;

/** 1 meter => 39.37008 inches */
export const METERS_TO_INCHES = 39.37008;

/** 1 meter => 3.28084 feet */
export const METERS_TO_FT = 3.28084;

/** 1 meter per sec => 2.236936 miles per hour */
export const MPS_TO_MPH = 2.236936;

// 1 meter per sec => 60 sec x 60 minutes / 1000 meters per km
export const MPS_TO_KPH = 3.6;

// miles per hour => km per hour
export const MPH_TO_KPH = 1.609344;

/** 1 inch => 2.54 centimeters */
export const IN_TO_CM = 2.54;

/** 1 inch => 25.4 millimeters */
export const IN_TO_MM = IN_TO_CM * 10;

export const MM_TO_FT = 1 / (IN_TO_MM * FT_TO_INCHES);

export const RPM_TO_RAD_PER_SEC = Math.PI / 30;

export const RAD_PER_SEC_TO_RPM = 1.0 / RPM_TO_RAD_PER_SEC;

export const DEG_TO_RAD = Math.PI / 180;

export const RAD_TO_DEG = 180 / Math.PI;

export const RAD_RIGHT_ANGLE = 0.5 * Math.PI;
export const RAD_FULL_ROTATION = 2 * Math.PI;

export const DEG_RIGHT_ANGLE = 90;

export const HR_TO_MIN = 60;
// 12 hours per 360 degrees
export const HR_TO_DEG = 360 / 12;

export const TAU = 2 * Math.PI;

// as defined in Python core_utils globals
export const BALL_CIRC_FT = 9.125 / 12;
export const BALL_DIAMETER_FT = BALL_CIRC_FT / Math.PI;
export const BALL_RADIUS_FT = BALL_DIAMETER_FT / 2;

/** returns ft and in of provided measurement in feet */
export const numberToImperial = (value_ft: number): IImperialCoordinate => {
  const sign = Math.sign(value_ft);
  const abs = Math.abs(value_ft);
  const floor = Math.floor(abs);

  // e.g. anything goes
  const rawInches = Math.round(FT_TO_INCHES * (abs - floor));
  // e.g. 0-11
  const safeInches = rawInches % FT_TO_INCHES;

  const feet = rawInches === FT_TO_INCHES ? floor + 1 : floor;

  const output: IImperialCoordinate = {
    originalFt: value_ft,
    nearestInchFt: sign * (feet + safeInches / FT_TO_INCHES),
    sign: sign,
    ft: feet,
    in: safeInches,
    display: `${sign === -1 ? '-' : ''}${feet}' ${safeInches}"`,
  };

  return output;
};

/** provide feet and/or inches measurements to pretty-print the result,
 * like 6.5 feet => 6' 6"
 * any defined and NaN parameters (i.e. not undefined) result in 'NaN input'
 */
export const imperialToText = (
  decimal_ft?: number,
  decimal_inches?: number
): string => {
  let totalFT = 0;

  if (decimal_ft !== undefined) {
    if (isNaN(decimal_ft)) {
      return 'NaN feet parameter';
    }

    totalFT += decimal_ft;
  }

  if (decimal_inches !== undefined) {
    if (isNaN(decimal_inches)) {
      return 'NaN inches parameter';
    }

    totalFT += decimal_inches / FT_TO_INCHES;
  }

  const sign = totalFT < 0 ? '-' : '';

  const absTotal = Math.abs(totalFT);
  const absFeet = Math.floor(absTotal);
  const absInches = Math.round((absTotal - absFeet) * FT_TO_INCHES);

  if (absInches > 0) {
    return `${sign}${absFeet}' ${absInches}"`;
  }

  return `${sign}${absFeet}'`;
};

// feet decimal to string e.g. 6.5 => 6' 6", rounds to nearest inch
// todo: can we combine this + the two above utils
export const feetFloatToString = (v?: Nullable<number>): string | undefined => {
  if (isNil(v)) {
    return undefined;
  }

  const sign = v < 0 ? '-' : '';
  const totalInches = Math.round(Math.abs(v) * FT_TO_INCHES);

  const feet = Math.floor(totalInches / FT_TO_INCHES);
  const inches = totalInches % FT_TO_INCHES;

  return `${sign}${feet}' ${inches}"`;
};

export const IDENTITY_MATRIX_2D = [
  [1, 0],
  [0, 1],
];

/**
 * get a matrix for rotation by radians in z-axis then y-axis then x-axis
 * @param rotation leave parameters undefined (or 0) to skip rotation by that axis
 * @returns
 */
export const getRotationMatrix = (rotation: {
  z_rad?: number;
  x_rad?: number;
  y_rad?: number;
}): Matrix3 => {
  // spawns a 3x3 identity matrix
  let output = new Matrix3();

  if (rotation.x_rad) {
    output = new Matrix3()
      .set(
        1,
        0,
        0,
        0,
        Math.cos(rotation.x_rad),
        -Math.sin(rotation.x_rad),
        0,
        Math.sin(rotation.x_rad),
        Math.cos(rotation.x_rad)
      )
      .multiply(output);
  }

  if (rotation.y_rad) {
    output = new Matrix3()
      .set(
        Math.cos(rotation.y_rad),
        0,
        Math.sin(rotation.y_rad),
        0,
        1,
        0,
        -Math.sin(rotation.y_rad),
        0,
        Math.cos(rotation.y_rad)
      )
      .multiply(output);
  }

  if (rotation.z_rad) {
    output = new Matrix3()
      .set(
        Math.cos(rotation.z_rad),
        -Math.sin(rotation.z_rad),
        0,
        Math.sin(rotation.z_rad),
        Math.cos(rotation.z_rad),
        0,
        0,
        0,
        1
      )
      .multiply(output);
  }

  return output;
};

// use case would be to get a rotation matrix that could take a global velocity (vx,vy,vz) = Vector3(vx,vy,vz) and localize its frame of reference
export const getLocalizeMatrix = (vector: Vector3) => {
  return getRotationMatrix({
    // No sign switch because vy is already negative
    z_rad: Math.tan(vector.x / vector.y),
    x_rad: Math.sin(vector.z / vector.length()),
  });
};

export const isBetween = (
  n: number,
  min: number,
  max: number,
  strictly = false
) => {
  if (strictly) {
    return n > min && n < max;
  }

  return n >= min && n <= max;
};

/** mean aka: average, provide a denominator to change what the sum is divided by */
export const mean = (values: number[], denominator?: number) => {
  /** no values */
  if (values.length === 0) {
    return NaN;
  }

  /** one or more invalid values */
  if (!values.every((v) => !isNaN(v))) {
    return NaN;
  }

  return values.reduce((p, c) => p + c, 0) / (denominator ?? values.length);
};

/** standard deviation */
export const std = (values: number[]) => {
  const avg = mean(values);

  /** invalid average */
  if (isNaN(avg)) {
    return NaN;
  }

  return Math.sqrt(
    values.map((v) => Math.pow(v - avg, 2)).reduce((a, b) => a + b) /
      values.length
  );
};

/** round something to a specific number of decimals */
export const round = (value: number, decimals: number) => {
  /** invalid parameters */
  if (isNaN(value) || isNaN(decimals)) {
    return NaN;
  }

  /** nonsense decimals => do nothing */
  if (decimals < 0) {
    return value;
  }

  const factor = Math.pow(10, Math.round(decimals));
  return Math.round(value * factor) / factor;
};

// does not modify the original matrix, may not be row reduced
export const getEchelonForm_2x2 = (
  input: number[][]
): number[][] | undefined => {
  try {
    // Check if the matrix is 2x2
    if (input.length !== 2 || !input.every((row) => row.length === 2)) {
      throw new Error('Input matrix must be a 2x2 matrix.');
    }

    if (!input.every((row) => !row.includes(NaN))) {
      throw new Error('Input matrix cannot contain NaN values.');
    }

    // make a copy of the values from input
    const output = input.map((row) => [row[0], row[1]]);

    // apply row operations to achieve row reduced echelon form
    if (output[0][0] === 0) {
      // swap rows if the first element in the first row is zero
      [output[0], output[1]] = [output[1], output[0]];
    }

    if (output[0][0] === 0) {
      throw new Error('First index of first row cannot be zero');
    }

    // scale the first index of first row to make the first element 1
    const scale = output[0][0];
    for (let j = 0; j < 2; j++) {
      output[0][j] /= scale;
    }

    // use row operations to make the second element in the first column zero
    const factor = output[1][0];
    for (let j = 0; j < 2; j++) {
      output[1][j] -= factor * output[0][j];
    }

    return output;
  } catch (e) {
    console.error({
      event: 'getEchelonForm_2x2 failed',
      input,
      error: e,
    });
    return undefined;
  }
};

// returns undefined whenever input cannot be successfully interpreted as a number
export const safeNumber = (
  value: string | undefined | null
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return undefined;
  }

  if (value === '') {
    return undefined;
  }

  const output = Number(value);
  if (isNaN(output)) {
    return undefined;
  }

  return output;
};

export const safeFloat = (
  value: string | undefined | null
): number | string | undefined => {
  if (isNil(value)) {
    return undefined;
  }

  if (value === '') {
    return undefined;
  }

  // allow a lone negative sign to facilitate first character of a negative number
  if (value === '-') {
    return value;
  }

  // allow input that end with a decimal point to facilitate typing decimal numbers
  if (/^-?\d+\.$/.test(value)) {
    return value;
  }

  const output = Number(value);
  if (isNaN(output)) {
    return undefined;
  }

  return output;
};

export const isNumber = (value: any): boolean => {
  if (value === undefined) {
    return false;
  }

  if (value === null) {
    return false;
  }

  if (isNaN(value)) {
    return false;
  }

  return typeof value === 'bigint' || typeof value === 'number';
};

export const isInteger = (value: any): boolean => {
  try {
    if (!isNumber(value)) {
      return false;
    }

    return Math.round(value) === value;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const convertUnits = (factor: number, value?: number) => {
  if (value !== undefined && !isNaN(value)) {
    return factor * value;
  }

  console.warn(
    `Non-numeric value cannot be converted, returning original value: ${value}`
  );
  return value;
};

/** returns undefined if the operation fails for whatever reason */
export const meanObject = <T>(
  objects: T[],
  silent?: boolean
): T | undefined => {
  try {
    const tokenObject = objects[0];
    if (!tokenObject) {
      throw new Error('MEAN OBJECT: no values to calculate mean of');
    }

    /** should be the same type as input */
    const output: any = { ...tokenObject };

    /** only calculate mean of numeric fields */
    const numKeys = Object.keys(tokenObject).filter(
      (key) => typeof tokenObject[key as keyof T] === 'number'
    );
    numKeys.forEach((key) => {
      const values = objects
        .filter((o) => typeof o[key as keyof T] === 'number')
        .map((o) => o[key as keyof T] as number);
      output[key] = mean(values);
    });

    return output;
  } catch (e) {
    if (!silent) {
      console.warn(e);
    }
    return undefined;
  }
};

// Log gamma function (Sterling approximation)
export const gammaLog = (x: number): number => {
  if (x <= 0) {
    return NaN;
  }

  const lnSqrt2Pi = 0.5 * Math.log(2 * Math.PI); // 0.5 * ln(2π)
  return (
    (x - 0.5) * Math.log(x) - x + lnSqrt2Pi + 1 / (12 * x) - 1 / (360 * x ** 3)
  );
};

// Continued fraction for incomplete beta (numerically stable)
export const betaContinuedFraction = (
  x: number,
  a: number,
  b: number
): number => {
  const MAX_ITER = 200;
  const EPS = 1e-14;
  const FPMIN = 1e-30;

  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;

  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < FPMIN) {
    d = FPMIN;
  }

  d = 1 / d;
  let h = d;

  for (let m = 1; m <= MAX_ITER; m++) {
    const m2 = 2 * m;

    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) {
      d = FPMIN;
    }

    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) {
      c = FPMIN;
    }

    d = 1 / d;
    h *= d * c;

    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;

    if (Math.abs(d) < FPMIN) {
      d = FPMIN;
    }

    c = 1 + aa / c;

    if (Math.abs(c) < FPMIN) {
      c = FPMIN;
    }

    d = 1 / d;
    const del = d * c;
    h *= del;

    if (Math.abs(del - 1) < EPS) {
      break;
    }
  }

  return h;
};

// Incomplete beta function (regularized)
export const betaIncomplete = (x: number, a: number, b: number): number => {
  const bt =
    x === 0 || x === 1
      ? 0
      : Math.exp(
          gammaLog(a + b) -
            gammaLog(a) -
            gammaLog(b) +
            a * Math.log(x) +
            b * Math.log(1 - x)
        );

  if (x < (a + 1) / (a + b + 2)) {
    return (bt * betaContinuedFraction(x, a, b)) / a;
  }

  return 1 - (bt * betaContinuedFraction(1 - x, b, a)) / b;
};

// Student's t CDF using incomplete beta
export const tCDF = (x: number, df: number): number => {
  const t = df / (x * x + df);
  const ib = betaIncomplete(t, df / 2, 0.5);

  return x < 0 ? 0.5 * ib : 1 - 0.5 * ib;
};

// Bisection-based PPF for t-distribution
export const tPPF = (
  p: number,
  df: number,
  tol = 1e-8,
  maxIter = 100
): number | undefined => {
  try {
    if (p <= 0 || p >= 1) {
      throw new Error('p must be in (0,1)');
    }

    let low = -8; // Smarter initial guess
    let high = 8;

    // Bisection method
    for (let i = 0; i < maxIter; i++) {
      const mid = (low + high) / 2;
      const cdfVal = tCDF(mid, df);

      if (Math.abs(cdfVal - p) < tol) {
        return mid;
      }

      if (cdfVal < p) {
        low = mid;
      } else {
        high = mid;
      }
    }

    throw new Error('tPPF did not converge');
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getStDev = (
  nums: number[],
  baseline_stdev: number,
  return_true_stdev = false
): number => {
  if (nums.length < 2) {
    return baseline_stdev;
  }

  const mean = nums.reduce((a, b) => a + b) / nums.length;
  const meas_stdev = Math.sqrt(
    nums.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) /
      (nums.length - 1)
  );

  if (return_true_stdev) {
    return meas_stdev;
  }

  const alpha = 20 / (19 + 4 * Math.pow(nums.length, 2));
  return alpha * baseline_stdev + (1 - alpha) * meas_stdev;
};

export const getMeanStdErr = (
  samples: number[],
  baseline_stdev: number,
  return_true_sem = false
): number => {
  if (samples.length === 0) {
    return 0;
  }

  return (
    getStDev(samples, baseline_stdev, return_true_sem) /
    Math.sqrt(samples.length)
  );
};

export const normalCdfMeanInRange = (
  samples: number[],
  baseline_stdev: number,
  target: number,
  threshold: number,
  return_true_sem = false
): number => {
  const sampleMean = mean(samples);
  const meanStdErr = getMeanStdErr(samples, baseline_stdev, return_true_sem);
  const upper = target + threshold;
  const lower = target - threshold;

  return (
    normalCDF((upper - sampleMean) / meanStdErr) -
    normalCDF((lower - sampleMean) / meanStdErr)
  );
};

export const tCdfMeanInRange = (
  samples: number[],
  baseline_stdev: number,
  target: number,
  threshold: number,
  return_true_sem = false
): number => {
  const sampleMean = mean(samples);
  const meanStdErr = getMeanStdErr(samples, baseline_stdev, return_true_sem);
  const upper = target + threshold;
  const lower = target - threshold;

  return (
    tCDF((upper - sampleMean) / meanStdErr, samples.length - 1) -
    tCDF((lower - sampleMean) / meanStdErr, samples.length - 1)
  );
};

export const normalCDF = (x: number): number => {
  const t = 1 / (1 + 0.2315419 * Math.abs(x));
  const d = 0.3989423 * Math.exp((-x * x) / 2);
  const prob =
    d *
    t *
    (0.3193815 +
      t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  if (x > 0) {
    return 1 - prob;
  }
  return prob;
};

/**
 * e.g. use bs vx/vy/vz to get speed
 * @param values
 * @returns in same units as input coordinates
 */
export const getMagnitude = (values: number[]) =>
  Math.sqrt(sum(values.map((v) => Math.pow(v, 2))));

export const gaussianRandom = (mean = 0, stdev = 1): number => {
  const u = 1 - Math.random(); // Convert [0,1) to (0,1]
  const v = Math.random();
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return z * stdev + mean;
};

// maps any number into something in [0,1); delta of zero will coalesce into 1
export const normSelf = (value: number, delta = 1) => {
  const absValue = Math.abs(value);
  return absValue / (absValue + Math.abs(delta || 1));
};

// mainly for filtering w/ dual sliders
export const isInRange = (
  range: [number, number] | null,
  value?: number | null
) => {
  // no filter applied
  if (isNil(range)) {
    return true;
  }

  // no value from record
  if (isNil(value)) {
    return false;
  }

  return isBetween(value, Math.min(...range), Math.max(...range));
};

/** Lehmer PRNG, if something needs to be random w/ deterministic start */
export const seededRandom = (seed: number) => {
  let state = seed % 2147483647;

  return () => {
    state = (state * 48271) % 2147483647;
    return state / 2147483647;
  };
};

// returns null instead of dividing by 0 into a NaN
export const safeGetRatio = (
  numerator: number,
  denominator: number
): Nullable<number> => {
  if (denominator === 0) {
    return null;
  }

  return numerator / denominator;
};
